import { useEffect, useRef, useState, useMemo } from "react";
import { useSnapshot } from "valtio";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import TWButton from "../../../../components/TWButton";
import { editorState } from "../../../../utils/editor";
import { capitalize } from "../../../../utils/basic.utils";
import FunnelSimpleThinIcon from "../../../../components/icons/FunnelSimpleThinIcon";
import { PiCheckBold } from "react-icons/pi";
import { IoClose } from "react-icons/io5";

interface GuestFilterDropdownProps {
  filters: GuestFilters;
  onFiltersChange: (filters: GuestFilters) => void;
}

export interface GuestFilters {
  rsvpComingFor?: string;
  relationship?: string;
  inviteStatus?: string;
}

const RSVP_COMING_FOR_OPTIONS = [
  { value: "bride", label: "Bride" },
  { value: "groom", label: "Groom" },
  { value: "both", label: "Both" },
];


const INVITE_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "accept", label: "Accepted" },
  { value: "reject", label: "Declined" },
];

const GuestFilterDropdown = ({
  filters,
  onFiltersChange,
}: GuestFilterDropdownProps) => {
  const { rsvpRelationships } = useSnapshot(editorState);

  const activeFiltersCount = useMemo(() => {
    return (
      (filters.rsvpComingFor ? 1 : 0) +
      (filters.relationship ? 1 : 0) +
      (filters.inviteStatus ? 1 : 0)
    );
  }, [filters]);

  const handleRSVPComingForSelect = (value: string) => {
    onFiltersChange({
      ...filters,
      rsvpComingFor: filters.rsvpComingFor === value ? undefined : value
    });
  };

  const handleRelationshipSelect = (relationship: string) => {
    onFiltersChange({
      ...filters,
      relationship: filters.relationship === relationship ? undefined : relationship
    });
  };

  const handleInviteStatusSelect = (status: string) => {
    onFiltersChange({
      ...filters,
      inviteStatus: filters.inviteStatus === status ? undefined : status
    });
  };

  const clearAllFilters = () => {
    onFiltersChange({
      rsvpComingFor: undefined,
      relationship: undefined,
      inviteStatus: undefined,
    });
  };

  const checkboxItemClasses =
    "relative flex cursor-default select-none items-center rounded-md px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-gray-100 data-[disabled]:pointer-events-none data-[disabled]:opacity-50";

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          className="flex flex-col items-center cursor-pointer hover:opacity-70 transition-opacity relative outline-none"
          aria-label="Open filters"
        >
          <FunnelSimpleThinIcon className="w-6 h-6" color="#6B7280" />
          {activeFiltersCount > 0 && (
            <span className="absolute -top-1 -right-1 flex items-center justify-center bg-black text-white rounded-full text-xs w-4 h-4">
              {activeFiltersCount}
            </span>
          )}
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          sideOffset={8}
          align="end"
          className="z-50 w-64 rounded-lg border bg-white p-2 shadow-md animate-in data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2 max-h-96 overflow-y-auto"
        >
          <div className="flex justify-between items-center px-2 py-1">
            <h3 className="text-base font-semibold">Filter Guests</h3>
            {activeFiltersCount > 0 && (
              <button
                onClick={clearAllFilters}
                className="text-xs font-medium text-blue-600 hover:underline"
              >
                Clear all
              </button>
            )}
          </div>
          <DropdownMenu.Separator className="h-px bg-gray-200 my-2" />

          {/* RSVP Coming For */}
          <DropdownMenu.Label className="px-2 py-1.5 text-xs font-semibold text-gray-500">
            RSVP Coming For
          </DropdownMenu.Label>
          {RSVP_COMING_FOR_OPTIONS.map((option) => (
            <DropdownMenu.CheckboxItem
              key={option.value}
              checked={filters.rsvpComingFor === option.value}
              onCheckedChange={() => handleRSVPComingForSelect(option.value)}
              className={checkboxItemClasses}
            >
              <span className="flex-grow">{option.label}</span>
              <DropdownMenu.ItemIndicator>
                <PiCheckBold />
              </DropdownMenu.ItemIndicator>
            </DropdownMenu.CheckboxItem>
          ))}
          <DropdownMenu.Separator className="h-px bg-gray-200 my-2" />

          {/* Relationship */}
          <DropdownMenu.Label className="px-2 py-1.5 text-xs font-semibold text-gray-500">
            Relationship
          </DropdownMenu.Label>
          {rsvpRelationships.map((relationship) => (
            <DropdownMenu.CheckboxItem
              key={relationship}
              checked={filters.relationship === relationship}
              onCheckedChange={() => handleRelationshipSelect(relationship)}
              className={checkboxItemClasses}
            >
              <span className="flex-grow">{capitalize(relationship)}</span>
              <DropdownMenu.ItemIndicator>
                <PiCheckBold />
              </DropdownMenu.ItemIndicator>
            </DropdownMenu.CheckboxItem>
          ))}
          <DropdownMenu.Separator className="h-px bg-gray-200 my-2" />

          {/* Invite Status */}
          <DropdownMenu.Label className="px-2 py-1.5 text-xs font-semibold text-gray-500">
            Invite Status
          </DropdownMenu.Label>
          {INVITE_STATUS_OPTIONS.map((option) => (
            <DropdownMenu.CheckboxItem
              key={option.value}
              checked={filters.inviteStatus === option.value}
              onCheckedChange={() => handleInviteStatusSelect(option.value)}
              className={checkboxItemClasses}
            >
              <span className="flex-grow">{option.label}</span>
              <DropdownMenu.ItemIndicator>
                <PiCheckBold />
              </DropdownMenu.ItemIndicator>
            </DropdownMenu.CheckboxItem>
          ))}

        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default GuestFilterDropdown;
